import { Link } from "react-router-dom";

const PrivacyPage = () => {
  return (
    <div className="doc-page">
      <nav className="doc-nav" data-testid="privacy-nav">
        <Link className="nav-logo" to="/" data-testid="nav-logo">
          <img src="/ruler-icon.png" alt="" className="nav-logo-icon-img-sm" />
          <span className="nav-logo-text-sm">Kids Size Finder</span>
        </Link>
        <Link className="nav-back" to="/" data-testid="nav-back-link">← Back to home</Link>
      </nav>

      <main className="doc-main" data-testid="privacy-content">
        <div className="doc-header">
          <div className="doc-title">Privacy Policy</div>
          <div className="doc-meta">Last updated: April 2026</div>
          <div className="doc-company">
            <strong>Controller:</strong> Outgrow Solutions Oy &nbsp;·&nbsp; <strong>Business ID:</strong> 3594133-7<br />
            <strong>Address:</strong> Hedelmäkatu 1 A, 33270 Tampere, Finland<br />
            <strong>Email:</strong> privacy@kidssizefinder.com
          </div>
        </div>

        <p>This Privacy Policy describes how Outgrow Solutions Oy ("we," "us," or "our") collects, stores, uses, and shares your personal information when you use Kids Size Finder — a mobile app that helps parents find the right clothing size for their children across 36+ brands.</p>
        <p><strong>Questions?</strong> Contact us at privacy@kidssizefinder.com.</p>

        <div className="highlight-box">
          <p><strong>Summary:</strong> We collect your account information and the child measurements you provide. We use this data only to generate size recommendations. We do not sell your data, share children's information with third parties for marketing, or use data for advertising.</p>
        </div>

        <h2>1. What Information Do We Collect?</h2>
        <h3>Account Information</h3>
        <p>When you create an account we collect your email address and authentication information via Apple Sign In or Google OAuth.</p>
        <h3>Child Profile Information</h3>
        <p>When you create a child profile we collect, with your consent as parent or legal guardian:</p>
        <ul>
          <li>Child's name or nickname (for profile identification)</li>
          <li>Date of birth (used to project growth between measurement updates using WHO growth curve data)</li>
          <li>Physical measurements: height, weight, waist, chest, hip, and foot length</li>
          <li>Preferred brands</li>
        </ul>
        <p>This information is collected solely to provide clothing size recommendations and is subject to additional protections under COPPA.</p>
        <h3>Usage Information</h3>
        <p>We automatically collect technical information including device type, operating system, and app usage events via PostHog analytics. Analytics events do not include children's personal information.</p>

        <h2>2. How Do We Process Your Information?</h2>
        <p>We process your personal information to:</p>
        <ul>
          <li>Facilitate account creation and authentication</li>
          <li>Provide clothing size recommendations for your child</li>
          <li>Project your child's measurements forward using WHO growth curve data</li>
          <li>Store your preferred brands and settings</li>
          <li>Improve the accuracy of our size recommendation engine</li>
          <li>Comply with legal obligations</li>
        </ul>

        <h2>3. Legal Bases (GDPR)</h2>
        <p><strong>Consent</strong> — You provide explicit consent when creating a child profile and confirming you are the parent or legal guardian.</p>
        <p><strong>Performance of a Contract</strong> — We process your information to deliver the size recommendation service you have requested.</p>
        <p><strong>Legal Obligations</strong> — We process information as required to comply with applicable law.</p>

        <h2>4. When and With Whom Do We Share Your Information?</h2>
        <p>We share information only with the following service providers, solely for operating our service:</p>
        <ul>
          <li><strong>PostHog</strong> — App analytics, configured for COPPA compliance. No children's personal data is included in analytics events.</li>
          <li><strong>Google / Apple</strong> — Authentication only. We do not share child profile data with Google or Apple.</li>
          <li><strong>MongoDB Atlas</strong> — Database hosting for account and child profile storage.</li>
          <li><strong>Railway</strong> — Backend infrastructure hosting.</li>
        </ul>
        <p><strong>Business Transfers</strong> — In connection with any merger, sale, or acquisition of Outgrow Solutions Oy, your information may be transferred as part of that transaction.</p>
        <p><strong>Children's Information</strong> — We do not sell, share, or disclose personal information about children under 13 to third parties for marketing, advertising, or any commercial purpose.</p>

        <h2>5. Cookies and Tracking</h2>
        <p>The Kids Size Finder mobile app does not use cookies. We use PostHog for app analytics, configured in compliance with COPPA. Analytics events do not include children's personal information.</p>

        <h2>6. Social Logins</h2>
        <p>You may register and log in using Apple Sign In or Google OAuth. We receive only the information necessary to create and authenticate your account — typically your email address and name. We do not receive access to your Apple or Google account beyond authentication.</p>

        <h2>7. International Data Transfers</h2>
        <p>Our servers are located in the United States. If you are accessing our Services from outside the United States, your information may be transferred to, stored, and processed in the United States. We ensure appropriate safeguards are in place for such transfers in compliance with GDPR.</p>

        <h2>8. How Long Do We Keep Your Information?</h2>
        <p>We retain your personal information for as long as your account remains active. When you delete a child profile, that child's data is permanently deleted within 30 days. When you delete your account, all associated data including all child profiles is permanently deleted within 30 days. Backup copies are purged within 90 days.</p>

        <h2>9. How Do We Keep Your Information Safe?</h2>
        <p>We implement appropriate technical and organisational security measures including encryption of all data in transit and at rest. Child profile data receives additional access controls.</p>

        <h2>10. Children's Privacy and COPPA Compliance</h2>
        <h3>A. Who May Create an Account</h3>
        <p>Kids Size Finder is not directed to children. Only adults aged 18 or older may create accounts. Children under 13 may not create accounts independently.</p>
        <h3>B. Parental Consent</h3>
        <p>By creating a child profile you confirm that you are at least 18 years of age and the parent or legal guardian of the child, and you consent to our collection and use of your child's measurement data for the sole purpose of generating clothing size recommendations.</p>
        <h3>C. Your Rights as a Parent</h3>
        <p>Under COPPA you have the right to:</p>
        <ul>
          <li>Review the personal information we have collected about your child</li>
          <li>Correct inaccurate information</li>
          <li>Delete your child's personal information at any time via the app or by contacting us</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <h3>D. How We Use Children's Information</h3>
        <p>We use children's measurement data exclusively to generate clothing size recommendations and project growth between visits using WHO growth curve data. We do not use children's information for advertising, marketing, or any purpose beyond size recommendations.</p>
        <h3>E. What We Do Not Do</h3>
        <ul>
          <li>We do not use children's information for targeted advertising</li>
          <li>We do not share children's information with third parties for marketing</li>
          <li>We do not allow third parties to contact your child</li>
          <li>We do not require more information than is necessary to provide size recommendations</li>
        </ul>

        <h2>11. Your Privacy Rights</h2>
        <p><strong>EEA, UK, and Switzerland residents</strong> have rights under GDPR and UK GDPR including access to your personal information, correction of inaccurate data, erasure of your data, restriction of processing, data portability, and objection to processing.</p>
        <p><strong>US residents</strong> — We do not sell personal information as defined by applicable US state privacy laws including the CCPA.</p>
        <p>To exercise any of these rights use the Settings screen in the app or contact us at privacy@kidssizefinder.com. We will respond within 30 days.</p>

        <h2>12. Updates to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. The updated version will be indicated by an updated date at the top of this page. We will notify you of material changes via email or in-app notification.</p>

        <h2>13. Contact Us</h2>
        <p>Outgrow Solutions Oy<br />Hedelmäkatu 1 A, Tampere, 33270, Finland</p>
        <p>Email: privacy@kidssizefinder.com</p>
        <p>To review, update, or delete your data: use the Settings screen in the app or email privacy@kidssizefinder.com. We will respond within 30 days.</p>
      </main>

      <footer className="doc-footer" data-testid="privacy-footer">
        <div className="footer-links">
          <Link to="/">Home</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <a href="mailto:support@kidssizefinder.com">Support</a>
        </div>
        <p className="footer-copy">© 2026 Outgrow Solutions Oy. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PrivacyPage;
